import moment from 'moment';
import { useContext, useEffect, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useTheme } from 'styled-components';
import { DATE_FORMAT, StripeAccountTransactionStatusesEnum } from 'dash-common';
import {
  Flex,
  Text,
  Icon,
  Badge,
  Button,
  Spinner,
  useScreenResolution,
  FlatPaginationContext,
  ViewerContext,
} from 'dash-ui';
import {
  useGetCurrentStripeAccountTransactionsQuery,
  useObserveCurrentAccountProductSubscriptionSubscription,
} from 'gql/generated.graphql';

import { TransactionsTable } from './TransactionsSection.styles';
import { getTransactionBadgeType } from './TransactionsSection.helpers';

function TransactionsSection() {
  const { account } = useContext(ViewerContext);
  const theme = useTheme();
  const { state: paginationState } = useContext(FlatPaginationContext);

  const { t: billingTableLabels } = useTranslation('tables', {
    keyPrefix: 'transactionHistory',
  });
  const { t: billingPageLabels } = useTranslation('pages', {
    keyPrefix: 'profilePage.billing',
  });
  const {
    devices: { isMobile, isTablet },
  } = useScreenResolution();

  const { data, loading, refetch } =
    useGetCurrentStripeAccountTransactionsQuery({
      variables: {
        flatPagination: {
          limit: paginationState.perPage,
          startingAfter: paginationState.startingAfter,
          endingBefore: paginationState.endingBefore,
        },
      },
    });

  useEffect(() => {
    refetch();
  }, [account]);

  const { transactions = [], hasMore } = useMemo(
    () => ({
      transactions: data?.getCurrentStripeAccountTransactions.collection || [],
      hasMore: data?.getCurrentStripeAccountTransactions.hasMore || false,
    }),
    [data]
  );

  const columns = useMemo(
    () => [
      {
        Header: () => <Text ellipsis>{billingTableLabels`invoiceName`}</Text>,
        accessor: 'invoiceName',
        name: 'invoiceName',
        Cell: ({ value }: { value: string }) => <Text ellipsis>{value}</Text>,
        ...((isMobile || isTablet) && { sticky: 'left', width: 191 }),
      },
      {
        Header: () => <Text ellipsis>{billingTableLabels`dateOfCharge`}</Text>,
        accessor: 'dateOfCharge',
        name: 'dateOfCharge',
        Cell: ({ value }: { value: Date }) => (
          <Text ellipsis>{moment(value).format(DATE_FORMAT)}</Text>
        ),
        ...((isMobile || isTablet) && { width: 30 }),
      },
      {
        Header: () => <Text ellipsis>{billingTableLabels`status`}</Text>,
        accessor: 'status',
        name: 'status',
        Cell: ({ value }: { value: StripeAccountTransactionStatusesEnum }) => (
          <Badge
            square
            whiteSpace="nowrap"
            type={getTransactionBadgeType(value)}
            title={billingTableLabels(`statuses.${value.toLowerCase()}`)}
          />
        ),
        ...((isMobile || isTablet) && { width: 30 }),
      },
      {
        Header: () => (
          <Flex justifyContent="right">
            <Text ellipsis>{billingTableLabels`amount`}</Text>
          </Flex>
        ),
        accessor: 'amount',
        name: 'amount',

        Cell: ({ value }: { value: number }) => (
          <Flex justifyContent="right">
            <Text>${value}</Text>
          </Flex>
        ),
        ...((isMobile || isTablet) && { width: 30 }),
      },
      {
        accessor: 'invoicePdfUrl',
        name: 'invoicePdfUrl',
        Cell: ({ value }: { value: string }) => (
          <Flex alignItems="center" justifyContent="center" ml={1}>
            <Icon
              size={18}
              name="download"
              color={theme.colors.b100}
              onClick={() => window.open(value, '_self')}
            />
          </Flex>
        ),
        ...((isMobile || isTablet) && { width: 30 }),
      },
    ],
    [isMobile, isTablet, theme.colors.b100, billingTableLabels]
  );
  const hasData =
    transactions.length ||
    paginationState.endingBefore ||
    paginationState.startingAfter;

  return hasData ? (
    <Flex mt={6} flexDirection="column" flex={1}>
      <Flex alignItems="center" justifyContent="space-between" w="100%">
        <Flex flex={1}>
          <Text bold lg={!isMobile}>
            {billingPageLabels('transactionHistory')}
          </Text>
        </Flex>
        <Button
          variation="flat"
          icon="download"
          iconProps={{ size: 18 }}
          px={0}
          disabled
        >
          {billingPageLabels('downloadAll')}
        </Button>
      </Flex>

      {loading ? (
        <Flex alignItems="center" justifyContent="center" py={4}>
          <Spinner color={theme.colors.b100} />
        </Flex>
      ) : (
        <TransactionsTable
          w="100%"
          overflow="auto"
          mt={4}
          tableProps={{
            data: transactions,
            columns,
            defaultColumn: {
              minWidth: 80,
              width: 190,
              maxWidth: 190,
            },
          }}
          targetPaginationField="invoiceId"
          paginationProps={{ hasMore }}
        />
      )}
    </Flex>
  ) : null;
}

export default TransactionsSection;
